/**
 * Parked driver data (sync_conflicts) — hooks for the In Review compare, the job badge and
 * the Sync page (OPEN-144 PR 3 + PR 4, DEC-357).
 *
 *   GET  /v1/sync-conflicts            list, keyset-paged (the Sync page walks every page
 *                                      with "Load more"; never silently page 1 only)
 *   GET  /v1/sync-conflicts/:id        one item + the side-by-side compare (duplicates)
 *   POST /v1/sync-conflicts/:id/keep   { reason? }   REVIEW_TICKET
 *   POST /v1/sync-conflicts/:id/apply  { reason }    EDIT_TICKET_VOLUME (OPEN-144 PR 2)
 *   POST /v1/sync-conflicts/:id/dismiss { reason }   REVIEW_TICKET, not for duplicates
 *
 * Every decision changes the review panel (flag, approve gate, after a swap the values and
 * acks), the job's badge and timeline, and the driver's sync badge, so each one invalidates
 * all of those. The API is the authority; role gates in the UI are UX only.
 */

import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import {
  apiErrorFromResponse,
  authedFetch,
  useApiMutation,
  useApiOpts,
  useApiQuery,
} from '@lodex-pro/web-shared';
import type {
  ComparePhoto,
  CompareTicket,
  SyncConflictDetail,
  SyncConflictListResponse,
} from '@lodex-pro/shared';
import { DRIVER_SYNC_HEALTH_QUERY_KEY } from '../drivers/use-driver-sync-health';

export const SYNC_CONFLICTS_KEY = ['sync-conflicts'] as const;

/** The compare's ticket shape, names included (OPEN-144 PR 2, #821). */
export type CompareTicketView = CompareTicket;
/** A photo on the saved ticket; `arrivedAfterPark` = may belong to either ticket (§11). */
export type ParkedPhoto = ComparePhoto;
export type SyncConflictDetailView = SyncConflictDetail;

export type SyncConflictStatusFilter = 'OPEN' | 'RESOLVED' | 'ALL';

export interface SyncConflictListParams {
  readonly status: SyncConflictStatusFilter;
  /** A conflict_type, or undefined for every kind. */
  readonly kind?: string;
  readonly jobId?: string;
}

/** The list, page by page ("Load more" walks to the end). */
export function useSyncConflicts(
  params: SyncConflictListParams,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<SyncConflictListResponse>> {
  const opts = useApiOpts();
  return useInfiniteQuery({
    queryKey: [...SYNC_CONFLICTS_KEY, 'list', params],
    enabled,
    staleTime: 15_000,
    initialPageParam: null as string | null,
    getNextPageParam: (last: SyncConflictListResponse) => last.nextCursor,
    queryFn: async ({ pageParam }): Promise<SyncConflictListResponse> => {
      const qs = new URLSearchParams({ status: params.status, limit: '50' });
      if (params.kind !== undefined) qs.set('kind', params.kind);
      if (params.jobId !== undefined) qs.set('jobId', params.jobId);
      if (pageParam !== null) qs.set('cursor', pageParam);
      const path = `/v1/sync-conflicts?${qs.toString()}`;
      const res = await authedFetch(opts, path);
      if (!res.ok) {
        throw await apiErrorFromResponse(res, path);
      }
      return (await res.json()) as SyncConflictListResponse;
    },
  });
}

/** One item with its compare. Disabled until `id` is set. */
export function useSyncConflict(
  id: string | null,
): ReturnType<typeof useApiQuery<SyncConflictDetailView>> {
  const opts = useApiOpts();
  return useApiQuery<SyncConflictDetailView>(opts, `/v1/sync-conflicts/${id ?? ''}`, {
    queryKey: [...SYNC_CONFLICTS_KEY, 'detail', id ?? ''],
    staleTime: 10_000,
    enabled: id !== null && id !== '',
  });
}

export interface DecisionResponse {
  readonly resolvedIds: readonly string[];
  readonly resolution: string;
  /** /apply only: the other open duplicates now SUPERSEDED. */
  readonly supersededIds?: readonly string[];
  /** /apply only: the job's In Review flags after the recompute. */
  readonly inreviewFlags?: readonly string[];
}

function useDecision<TBody>(
  id: string,
  action: 'keep' | 'apply' | 'dismiss' | 'apply-site-change',
  onDone: (r: DecisionResponse) => void,
): ReturnType<typeof useApiMutation<TBody, DecisionResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TBody, DecisionResponse>(
    opts,
    `/v1/sync-conflicts/${id}/${action}`,
    'POST',
    {
      onSuccess: (r) => {
        void qc.invalidateQueries({ queryKey: SYNC_CONFLICTS_KEY });
        // The review panel: flag, approve gate, and after a swap the values and acks.
        void qc.invalidateQueries({ queryKey: ['inreview'] });
        // The job modal's badge and timeline.
        void qc.invalidateQueries({ queryKey: ['job-detail'] });
        void qc.invalidateQueries({ queryKey: ['jobs'] });
        // #60 — a site change also moves the timeline and the change-site pickers.
        void qc.invalidateQueries({ queryKey: ['job-audit'] });
        void qc.invalidateQueries({ queryKey: ['job-site-options'] });
        // A resolved row turns the driver's sync badge back from red.
        void qc.invalidateQueries({ queryKey: DRIVER_SYNC_HEALTH_QUERY_KEY });
        onDone(r);
      },
    },
  );
}

type DecisionMutation<TBody> = ReturnType<typeof useApiMutation<TBody, DecisionResponse>>;

/** Keep the saved ticket (resolves every open duplicate on the job). Reason optional. */
export function useKeepParked(
  id: string,
  onDone: (r: DecisionResponse) => void,
): DecisionMutation<{ reason?: string }> {
  return useDecision<{ reason?: string }>(id, 'keep', onDone);
}

/** Use the parked ticket (the swap; OPEN-144 PR 2). Reason required. */
export function useApplyParked(
  id: string,
  onDone: (r: DecisionResponse) => void,
): DecisionMutation<{ reason: string }> {
  return useDecision<{ reason: string }>(id, 'apply', onDone);
}

/** Dismiss a non-duplicate parked item. Reason required. */
export function useDismissConflict(
  id: string,
  onDone: (r: DecisionResponse) => void,
): DecisionMutation<{ reason: string }> {
  return useDecision<{ reason: string }>(id, 'dismiss', onDone);
}

/** #60 (DEC-365) — make a driver's parked site change, as the office. Reason required. */
export function useApplySiteChange(
  id: string,
  onDone: (r: DecisionResponse) => void,
): DecisionMutation<{ reason: string }> {
  return useDecision<{ reason: string }>(id, 'apply-site-change', onDone);
}
